// Вкладка «Хроника» кампании: ростер событий, карточки глав,
// иллюстрации, литературный текст. Вынесено из CampaignSetupPage.tsx
// без изменения поведения (Фаза 1). NB: hasChapterLiteraryText и др.
// одноимённы с cycle/ChronicleSection, но это независимые копии.

import { useState, useEffect } from "react";
import {
  api,
  type CampaignRosterEvent,
  type CampaignContinuity,
  type CampaignThreatOverview,
  type ChronicleChapter,
  type ChronicleEvent,
  type MediaAsset,
  type World,
} from "../api";
import { ContinuityHistory, ThreatHistory } from "./WorldDomainSections";
import {
  BlockHeader,
  ParagraphText,
  Spinner,
  type ChronicleSection,
  type PageAction,
} from "./shared";

function ChronicleTab({
  campaignId,
  world,
  continuity,
  threats,
  chapters,
  rosterEvents,
  section,
  canManage,
  action,
}: {
  campaignId: string;
  world: World;
  continuity: CampaignContinuity;
  threats: CampaignThreatOverview;
  chapters: ChronicleChapter[];
  rosterEvents: CampaignRosterEvent[];
  section: ChronicleSection;
  canManage: boolean;
  action: PageAction;
}) {
  if (section === "prologue")
    return (
      <div className="cc-tab-stack">
        <section className="cc-block cc-prologue-block">
          <BlockHeader
            eyebrow="Общая история"
            title="Пролог эпохи основания"
            icon="book"
          />
          <ParagraphText
            className="narrative-copy"
            text={world.openingNarrative}
          />
        </section>
        <IllustrationPanel
          campaignId={campaignId}
          world={world}
          canManage={canManage}
          action={action}
        />
      </div>
    );

  if (section === "threads")
    return <ContinuityHistory continuity={continuity} />;
  if (section === "threats") return <ThreatHistory threats={threats} />;

  if (section === "council")
    return (
      <div className="cc-tab-stack cc-roster-history">
        <section className="cc-block">
          <BlockHeader
            eyebrow="История состава"
            title="Как менялся Совет"
            icon="users"
          />
          <p className="cc-form-intro">
            Каждое присоединение сохраняется отдельно: почему Совет расширился,
            какая должность появилась и чьи обязанности были перераспределены.
          </p>
        </section>
        {rosterEvents.length === 0 ? (
          <section className="cc-block empty-state">
            <h3>Состав Совета пока не менялся</h3>
          </section>
        ) : (
          rosterEvents.map((event) => (
            <RosterEventCard event={event} key={event.id} />
          ))
        )}
      </div>
    );

  return (
    <div className="cc-tab-stack cc-campaign-chronicle">
      <section className="cc-block">
        <BlockHeader
          eyebrow="Долговременная история"
          title="Главы кампании"
          icon="book"
        />
        <p className="cc-form-intro">
          Каждый завершённый цикл сохраняется отдельной неизменяемой главой:
          исходная ситуация, ключевые события, решения Совета, последствия и
          изменения мира.
        </p>
      </section>
      {chapters.length === 0 ? (
        <section className="cc-block empty-state">
          <h3>Первая глава ещё не завершена</h3>
          <p>После публикации итогов цикла она появится здесь.</p>
        </section>
      ) : (
        chapters.map((chapter) => (
          <ChronicleChapterCard campaignId={campaignId} chapter={chapter} key={chapter.id} />
        ))
      )}
    </div>
  );
}
function RosterEventCard({ event }: { event: CampaignRosterEvent }) {
  return (
    <article className="cc-block cc-roster-event">
      <header className="cc-roster-event-heading">
        <div>
          <small>Изменение состава Совета</small>
          <h2>{event.title}</h2>
        </div>
        <time>
          {new Date(event.createdAt).toLocaleString("ru-RU", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </time>
      </header>
      <ParagraphText className="narrative-copy" text={event.narrativeText} />
      {event.reasonText && (
        <section className="cc-roster-event-reason">
          <h3>Почему изменились обязанности</h3>
          <ParagraphText text={event.reasonText} />
        </section>
      )}
      <div className="cc-role-change-list">
        {event.roleChanges.map((change) => (
          <article key={`${event.id}-${change.userId}`}>
            <div className="cc-role-change-title">
              <strong>{change.displayName}</strong>
              <span>
                {change.previousTitle
                  ? `${change.previousTitle} → ${change.newTitle}`
                  : change.newTitle}
              </span>
            </div>
            <p>{change.newDuties}</p>
            {change.explanation && <small>{change.explanation}</small>}
          </article>
        ))}
      </div>
      {event.consequenceText && (
        <section className="cc-roster-event-consequence">
          <h3>Что изменилось</h3>
          <ParagraphText text={event.consequenceText} />
        </section>
      )}
    </article>
  );
}
function hasChapterLiteraryText(event: ChronicleEvent): boolean {
  return chapterLiteraryText(event).trim().length > 0;
}
function chapterLiteraryText(event: ChronicleEvent): string {
  return deduplicateChapterText([event.narrativeText, event.reactionText]);
}
function deduplicateChapterText(parts: Array<string | undefined>): string {
  const seen = new Set<string>();
  const paragraphs: string[] = [];
  parts
    .map((text) => text?.trim() ?? "")
    .filter(Boolean)
    .forEach((part) => {
      part
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
        .filter(Boolean)
        .forEach((paragraph) => {
          const key = chapterParagraphKey(paragraph);
          if (seen.has(key)) return;
          seen.add(key);
          paragraphs.push(paragraph);
        });
    });
  return paragraphs.join("\n\n");
}
function chapterParagraphKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[«»"“”'.,:;!?—–\-()[\]]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function ChronicleChapterCard({
  campaignId,
  chapter,
}: {
  campaignId: string;
  chapter: ChronicleChapter;
}) {
  return (
    <article className="cc-block cc-chronicle-chapter">
      <header className="cc-chapter-heading">
        <div>
          <small>Цикл {chapter.cycleNumber}</small>
          <h2>{chapter.title}</h2>
        </div>
        <time>{new Date(chapter.createdAt).toLocaleDateString("ru-RU")}</time>
      </header>
      {chapter.recapImages.length > 0 && (
        <div className="prologue-gallery cc-chapter-recap-gallery">
          {chapter.recapImages.map((asset) => (
            <ChronicleChapterRecapAsset
              campaignId={campaignId}
              asset={asset}
              key={asset.id}
            />
          ))}
        </div>
      )}
      <section>
        <h3>С чего всё началось</h3>
        <ParagraphText
          className="narrative-copy"
          text={chapter.openingSummary}
        />
      </section>
      {chapter.keyEvents.length > 0 && (
        <section>
          <h3>Ключевые события</h3>
          <div className="cc-chapter-events">
            {chapter.keyEvents.filter((event) => event.eventType !== "DAY_OUTCOME" && hasChapterLiteraryText(event)).map((event) => (
              <article key={event.id}>
                <small>{event.title}</small>
                <ParagraphText text={chapterLiteraryText(event)} />
              </article>
            ))}
          </div>
        </section>
      )}
      {chapter.decisions.length > 0 && (
        <section>
          <h3>Решения Совета</h3>
          <div className="cc-chapter-decisions">
            {chapter.decisions.map((decision) => (
              <article key={decision.initiativeId}>
                <header>
                  <strong>{decision.title}</strong>
                  <span>{chronicleDecisionStatus(decision.status)}</span>
                </header>
                {decision.explanation && (
                  <ParagraphText text={decision.explanation} />
                )}
              </article>
            ))}
          </div>
        </section>
      )}
      {chapter.worldChanges.length > 0 && (
        <section>
          <h3>Что изменилось</h3>
          <div className="cc-change-summary-list">
            {chapter.worldChanges.map((change, index) => (
              <article key={`${change.code}-${index}`}>
                <strong>{change.displayName}</strong>
                <span>{worldChangeDescription(change)}</span>
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="cc-chapter-closing">
        <h3>Итог дня</h3>
        <ParagraphText
          className="narrative-copy"
          text={chapter.closingNarrative}
        />
      </section>
    </article>
  );
}
function ChronicleChapterRecapAsset({
  campaignId,
  asset,
}: {
  campaignId: string;
  asset: MediaAsset;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (asset.status !== "READY") {
      setSrc(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    void api
      .cycleImageContent(campaignId, asset.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaignId, asset.id, asset.status]);

  return (
    <figure className="prologue-image-card cc-chapter-recap-card">
      {src ? (
        <img src={src} alt="Комикс-итог главы" />
      ) : (
        <div className="prologue-image-placeholder">
          {asset.status === "FAILED" ? "Комикс-итог не удалось создать" : "Создаём комикс-итог главы…"}
        </div>
      )}
    </figure>
  );
}
function chronicleDecisionStatus(status: string) {
  return (
    (
      {
        RESOLVED: "Реализована",
        NOT_FUNDED: "Поддержана без финансирования",
        REJECTED: "Отклонена",
        ACCEPTED: "Принята",
        VOTING: "На голосовании",
      } as Record<string, string>
    )[status] ?? status
  );
}
function IllustrationPanel({
  campaignId,
  world,
  canManage,
  action,
}: {
  campaignId: string;
  world: World;
  canManage: boolean;
  action: PageAction;
}) {
  const assets = world.openingImages;
  if (assets.length === 0)
    return (
      <section className="cc-block">
        <BlockHeader
          eyebrow="Визуальная хроника"
          title="Иллюстрация пролога"
          icon="image"
        />
        <ImageProgress status="PENDING" />
      </section>
    );
  return (
    <section className="cc-block">
      <BlockHeader
        eyebrow="Визуальная хроника"
        title="Иллюстрация пролога"
        icon="image"
      />
      <div className="prologue-gallery">
        {assets.map((asset) => (
          <IllustrationAsset
            key={asset.id}
            campaignId={campaignId}
            worldName={world.worldName}
            asset={asset}
            canManage={canManage}
            action={action}
          />
        ))}
      </div>
    </section>
  );
}
function IllustrationAsset({
  campaignId,
  worldName,
  asset,
  canManage,
  action,
}: {
  campaignId: string;
  worldName: string;
  asset: MediaAsset;
  canManage: boolean;
  action: PageAction;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (asset.status !== "READY") return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void api
      .worldOpeningImageContent(campaignId, asset.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((cause) => setError("Иллюстрацию пока не удалось загрузить."));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaignId, asset.id, asset.status]);

  if (asset.status === "READY")
    return (
      <figure className="prologue-image-card">
        {src ? (
          <img src={src} alt={`Иллюстрация пролога ${worldName}`} />
        ) : (
          <div className="prologue-image-placeholder">
            <Spinner />
            Загружаем готовую иллюстрацию…
          </div>
        )}
        {error && <figcaption>{error}</figcaption>}
        {canManage && (
          <button
            type="button"
            className="cc-image-regenerate"
            onClick={() =>
              void action("Создаём новую версию иллюстрации…", () =>
                api.regenerateWorldImage(campaignId, asset.id),
              )
            }
          >
            Создать другую иллюстрацию
          </button>
        )}
      </figure>
    );
  if (asset.status === "FAILED")
    return (
      <figure className="prologue-image-card">
        <div className="prologue-image-placeholder failed">
          Иллюстрацию пока не удалось создать
        </div>
        <figcaption>История и игровые возможности уже доступны.</figcaption>
        {canManage && (
          <button
            type="button"
            className="cc-image-regenerate"
            onClick={() =>
              void action("Пробуем создать иллюстрацию снова…", () =>
                api.regenerateWorldImage(campaignId, asset.id),
              )
            }
          >
            Попробовать снова
          </button>
        )}
      </figure>
    );
  return (
    <figure className="prologue-image-card">
      <ImageProgress status={asset.status} />
    </figure>
  );
}
function ImageProgress({ status }: { status: MediaAsset["status"] }) {
  const label =
    status === "PENDING"
      ? "Иллюстрация ждёт создания"
      : status === "GENERATING"
        ? "Создаём иллюстрацию"
        : status === "RETRY"
          ? "Продолжаем создание иллюстрации"
          : status === "READY"
            ? "Иллюстрация готова"
            : "Не удалось создать иллюстрацию";
  return (
    <div className="cc-image-progress">
      <div className="cc-progress-icon">
        <Spinner />
      </div>
      <div>
        <strong>{label}</strong>
        <p>
          Она появится здесь автоматически. Пока можно перейти в другой раздел.
        </p>
      </div>
    </div>
  );
}
function worldChangeDescription(change: { kind?: string; before?: number; after?: number; delta: number }) {
  const amount = Math.abs(change.delta);
  if (change.delta === 0) {
    return typeof change.before === "number" && typeof change.after === "number"
      ? `Значение не изменилось (${change.before} → ${change.after}).`
      : "Состояние не изменилось.";
  }
  if (typeof change.before === "number" && typeof change.after === "number") {
    const arrow = `${change.before} → ${change.after}`;
    if (change.kind === "RESOURCE") {
      return change.delta > 0
        ? `Запасы выросли на ${amount} ${resourceUnit(amount)} (${arrow}).`
        : `Запасы снизились на ${amount} ${resourceUnit(amount)} (${arrow}).`;
    }
    if (change.kind === "INDICATOR") {
      return change.delta > 0
        ? `Показатель вырос на ${amount} ${pointUnit(amount)} (${arrow}).`
        : `Показатель снизился на ${amount} ${pointUnit(amount)} (${arrow}).`;
    }
    if (change.kind === "INSTITUTION_LOAD") {
      return change.delta > 0
        ? `Нагрузка выросла на ${amount} (${arrow}).`
        : `Нагрузка снизилась на ${amount} (${arrow}).`;
    }
    if (change.kind === "FACTION_ATTITUDE") {
      return change.delta > 0
        ? `Отношение улучшилось на ${amount} (${arrow}).`
        : `Отношение ухудшилось на ${amount} (${arrow}).`;
    }
  }
  const degree = amount >= 10 ? "резко" : amount >= 4 ? "заметно" : "немного";
  return change.delta > 0
    ? `Состояние ${degree} улучшилось.`
    : `Состояние ${degree} ухудшилось.`;
}
function resourceUnit(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "единицу";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "единицы";
  return "единиц";
}
function pointUnit(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "пункт";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "пункта";
  return "пунктов";
}

export {
  ChronicleTab,
  RosterEventCard,
  hasChapterLiteraryText,
  chapterLiteraryText,
  deduplicateChapterText,
  chapterParagraphKey,
  ChronicleChapterCard,
  ChronicleChapterRecapAsset,
  chronicleDecisionStatus,
  IllustrationPanel,
  IllustrationAsset,
  ImageProgress,
  worldChangeDescription,
  resourceUnit,
  pointUnit,
};
